import { localeNumberFormat, t } from '@ctcells/i18n'
import { useState, type ReactNode } from 'react'

import { cx } from '../../atoms/cx.ts'
import { StaleMark } from '../../atoms/StaleMark.tsx'
import { DeckRackFigure } from '../../figures/DeckRackFigure.tsx'

import { RackAxesFrame, ReagentList, SlotGrid, Storage, slotGridNote } from './SupplyDetail.tsx'
import type { ReplenishView } from './SupplyReplenishDialog.tsx'
import {
  reagentBayName,
  reagentPresentText,
  remainingText,
  supplyHint,
  supplyName,
  supplyOriginText,
} from './supplyText.ts'
import {
  isTipsLow,
  reagentRackChoices,
  supplyAlert,
  reagentRackCounts,
  reagentRackFigure,
  type ReagentBay,
  type ReagentRackChoice,
  type SupplyItem,
  type SupplyKey,
  type SupplySummary,
} from './supplySummary.ts'

import { useBoxHeightVar } from './useBoxMeasure.ts'

/* 펴는 칸 머리의 꺾쇠(`.a-disc__chev`) — 접힌 줄 원자의 것을 그대로 쓴다(FoldHead) */
import '../../atoms/disclosure.css'
import './supply.css'
import './supplyPanel.css'

/** 칸별 기록과 합계가 어긋났다는 표지 — 글리프 + 글자(색만으로 말하지 않는다) */
const MISMATCH_GLYPH = '≠'

const NUM = localeNumberFormat({ maximumFractionDigits: 1 })

/**
 * **모니터링 오른쪽 열 「소모품」 탭 — 소모품 현황 판**(2026-10-02 eric 「오른쪽은 소모품과 비전」 · 처음은 2026-10-01 CTCells 회의 「기존 우측 카메라 영역을 소모품으로 대체한다 …
 * 장비별 보기의 소모품 그림 UI(팁랙, 큐벳, 셰이커 등)를 시료별 보기 우측에 그대로 배치. 사용한 칸은 색상 변화로 표시해
 * 잔량을 직관적으로 파악」).
 *
 * ```
 * (판 자체는 카드가 아니다 · 접근 이름 「소모품 현황」 · 같은 크기 칸 여섯이 2열 × 3행으로 판 키를 똑같이 나눈다)
 * ┌ tc rack     6/9 (어셈블러) ┐ ┌ tip rack 1  16/96 ▲ (염색기) ┐  ← 칸 머리 — 굵은 이름 · 표지 → 오른쪽 끝 수치 · ▲ · 장비 칩
 * │ [ TC rack 그림 ]           │ │ [ 팁 랙 그림 ]                │
 * └────────────────────────────┘ └───────────────────────────────┘
 * ┌ tip rack 2  96/96 (염색기) ┐ ┌ tube rack 1   1/2 (염색기)   ┐  ← 시약 랙 칸 — 이름(· 보관 온도) · 있음/시약 수 · 장비 칩
 * │ [ 팁 랙 그림 ]             │ │ [랙 그림] │ 워싱용액 / A1 ●   │  ← 왼쪽 = 랙 그림 · 오른쪽 = 그 랙의 용액 목록(길면 목록 상자 안에서만 구른다)
 * └────────────────────────────┘ └───────────────────────────────┘
 * ┌ tube rack 2   0/3 (염색기) ┐ ┌ cold rack 4℃  1/3 (염색기)  ┐
 * └────────────────────────────┘ └───────────────────────────────┘
 *                                    ● 있음 ○ 없음 ▨ 미수신   ← 범례(`ReagentKeys`) — 판 맨 아래 한 번
 * ```
 * 🔴 **소모품 판은 같은 크기 칸 여섯이다**(eric 2026-10-06 「소모품을 분할로 하고.. 6개에 각각 넣어줘.. tc rack은.. 2개로 쪼개지고..
 *    시약은 (3)개 영역을 차지하게 되겠지?」) — tc rack 이 혼자 쓰던 줄이 tc rack · tip rack 1 로 쪼개지고, 시약은 랙 고르기(구획 선택기)를
 *    걷고 랙 셋(tube rack 1 · tube rack 2 · cold rack — `deckRacks.ts`)이 칸 셋을 하나씩 차지한다. 랙마다 머리에 그 랙의 수치(있음/시약 수)가 선다.
 *    ⚠ 종전(2026-10-02 「3개 랙 중에.. 하나식 선택해서」)의 `Segmented` 랙 고르기 · 선택지 수치 · 「시약」 한 카드는 걷었다. 되살리지 마라.
 *    데크 표에 없는 시약대(레시피에만 있는 자리)도 제 칸을 하나 받는다(칸이 일곱이 되면 판이 행을 하나 더 나눈다).
 *
 * 🔴 **항목마다 비전 탭의 영상 칸과 같은 카드다**(eric 2026-10-02 「이거 소모품도... 이거처럼... 사각형 안에 위에 소모품 이름 적고,
 *    아래에는 지금 같은 그림 형태로 표현해줘」 — `VisionColumn` 의 `.viscol__cell`). 흰 면 · 1px 테 · 패널 모서리 · 머리 줄 + 그림.
 *    종전의 판 한 장(흰 카드) · 그 안의 묶음 라벨(어셈블러 · 염색기)은 걷었다 — 카드 안에 카드가 되지 않게. 장비 이름은 카드 머리의
 *    오른쪽 끝 중립 칩으로 접었다(eric 같은 날 「보충을 빼고.. 어셈블러 칩을 맨 오른쪽에 넣어줘.. 팁 랙 1,2 도 마찬가지.. 시약도..」 —
 *    그 앞 판은 「TC 적재소 오른쪽에.. 어셈블러를 칩으로」였다). 칩은 상태가 아니라 분류다 — 색 없이 테 · 옅은 면만.
 *    카드의 접근 이름에도 장비 이름이 든다.
 * 🔴 **새 그림을 만들지 않는다** — 칸 격자는 `SupplyDetail.tsx` 의 `SlotGrid` 를(TC 는 그 안에서 장비 탭의 `DeckRackFigure`), 시약은 같은 파일의 줄 목록 `ReagentList` 를,
 *    이름 · 표지는 아래 `SupplyChipContent`(종전 요약 카드의 글자) 를, 수치는 `SupplyAmount` 를 부른다.
 * 🔴 **값을 새로 셈하지 않는다** — 받은 `supplySummary()` 를 그린다. 칸 상태는 `rackOccupancy()`(= `deckOccupancy`)가 정한다 —
 *    0 이면 전부 빈 칸, 가득이면 전부 찬 칸, 그 사이는 어느 칸인지 모르므로 가득 + 「칸별 사용 기록 미수신」 줄이다.
 *    색만으로 말하지 않는다: 머리의 「16/96」과 「▲ 부족」 글자가 같은 사실을 말한다.
 * 🔴 **머리는 누르는 것이 아니다**(eric 2026-10-02 「시약이나 소모품 클릭하면 팝업이 나오는데.. 안 나오게 해줘」) — 종전의
 *    머리 단추 · `›` · 상세 팝오버(`SupplyPopover`)를 걷었다. 팝오버는 되살리지 마라.
 *    ⚠ 2026-10-06 부터 그림이 있는 칸은 누르면 «그 자리에서» 펴지고, 2026-10-07 부터 그 칸 머리 끝에 펼침 꺾쇠가 선다
 *    (eric 「누른다는 느낌이 있는 아이콘 같은 거 넣어줘」 · `FoldHead`). 꺾쇠는 팝오버 표지가 아니라 펼침 표지다.
 * 🔴 **「보충」 버튼은 판에 없다**(eric 2026-10-02 「보충을 빼고.. 보충은 빼고..」) — 카드 머리 오른쪽 끝은 장비 칩이다.
 *    ⚠ 그래서 화면에서 보충으로 가는 길이 지금 **없다**(팝오버 · 요약 카드도 걷혔다). 「넣었습니까?」 확인 창은 회의 결정이라
 *    `SupplyReplenishButton` · `SupplyReplenishDialog` · `useSupplyReplenish` 는 지우지 않고 남겼다. 되살릴 때는 아래 `replenish`
 *    값(컨테이너가 계속 짓는다)을 `PanelItem` 머리에 다시 넘기면 된다.
 * 🔴 **소모품 현황은 이 판 한 곳이다**(eric 2026-10-02 「장비별 보기에서.. 이게 보이는데.. 이거 없애줘.. 시료로 방금 오른쪽으로
 *    뺐잖아」) — 선택기 줄의 요약 카드(`SupplyStrip`)는 걷었다. 2026-10-02 오후부터 장비별 보기에서도 오른쪽 열에 선다(왼쪽과 독립).
 *    시료 서랍이 열려 오른쪽 열이 접힌 동안에는 소모품이 화면에 없다.
 */
export function SupplyPanel({
  summary,
}: {
  summary: SupplySummary
  /**
   * 항목의 보충 값(팁 · TC). ⚠ **지금은 그리지 않는다**(eric 2026-10-02 「보충을 빼고..」) — 받기만 한다.
   * 컨테이너 · 화면의 배선은 되살릴 때를 위해 남겨 둔다(위 머리말).
   */
  replenish?: ((key: SupplyKey) => ReplenishView | undefined) | undefined
}) {
  const reagentItem = summary.items.find((entry) => entry.key === 'reagents')
  /*
   * 🔴 도는 작업이 없어도 랙 칸은 선다(eric 2026-10-03 「시약 부분도 디폴트로 그림이 보이게 해줘.. 작업이 안 도니 어떤 항체
   *    용액인지는 안 나오겠지만」) — 랙은 데크에 실제로 있는 장비의 구성 요소다(`deckRacks.ts`). 레시피가 없으면 시약 묶음이 비어
   *    칸 셋 다 `bay` 없이 선다(수치 「—」 · 그림 기본 모양 · 용액 목록 자리는 사유 한 줄)
   */
  const racks =
    reagentItem === undefined
      ? []
      : reagentRackChoices(reagentItem, summary.reagentBays, summary.storageSetpointCelsius)
  const noRecipe = reagentItem !== undefined && reagentItem.remaining === null
  return (
    /*
     * 🔴 보이는 제목 「소모품 현황」은 걷었다(eric 2026-10-02 「소모품 현황 텍스트는 빼고.. 중복이니」 — 위 탭 「소모품」과 겹친다).
     *    판의 접근 이름은 남긴다 — 스크린리더는 여전히 「소모품 현황」 구역으로 읽는다.
     * 차례는 `SUPPLY_KEYS` 그대로(eric 2026-09-28 「어셈블러 TC 적재소, 염색기 팁 랙 1, 팁 랙 2, 시약」) — 시약 자리에 랙 칸 셋이 차례대로 선다.
     */
    <section aria-label={t('monitoring.supply.panel')} className="suppanel">
      {summary.items.length === 0 ? (
        <p className="supdetail__note">{t('state.empty.generic')}</p>
      ) : (
        <ul className="suppanel__list">
          {summary.items.flatMap((item) =>
            item.key === 'reagents' && racks.length > 0
              ? racks.map((rack) => (
                  <RackItem
                    item={item}
                    key={`rack:${rack.key}`}
                    noRecipe={noRecipe}
                    rack={rack}
                    summary={summary}
                  />
                ))
              : [<PanelItem item={item} key={item.key} summary={summary} />],
          )}
        </ul>
      )}
      {/*
       * 범례 — 판 맨 아래 한 번(eric 2026-10-06 · 종전 시약 카드 맨 아래 「reagent 맨 밑에」를 판 맨 아래로). 랙 칸 셋의 그림 · 칸 표지를
       * 글자로 푼다. 도는 작업이 없으면 풀 점이 없어 서지 않는다.
       * 판 아래 범례 줄(● 남음 ○ 빈 자리 ■ 적재 □ 빈 칸 · 팁 · TC 뜻)은 걷었다(eric 2026-10-02 「이건 삭제해줘」) — 되살리지 마라.
       */}
      {racks.length > 0 && !noRecipe ? <ReagentKeys /> : null}
    </section>
  )
}

/** 랙 칸 이름 — 데크 랙이면 그 이름(tube rack 1 · tube rack 2 · cold rack), 레시피에만 있는 시약대면 시약대 이름 */
function rackChoiceName(choice: ReagentRackChoice): string {
  return choice.rack?.aria() ?? (choice.bay === undefined ? '' : reagentBayName(choice.bay))
}

function PanelItem({ item, summary }: { item: SupplyItem; summary: SupplySummary }) {
  const noRecipe = item.key === 'reagents' && item.remaining === null
  const low = isTipsLow(item)
  const moduleName = t(`module.${item.module}`)
  /* 접고 펴는 칸은 tc rack · tip rack(그림 + 구성 목록)뿐이다 — 시약 목록만 있는 칸 · 사유 한 줄은 접을 것이 없다 */
  const foldable = !noRecipe && item.key !== 'reagents'
  const [open, setOpen] = useState(false)
  const head = (
    <FoldHead foldable={foldable} open={open} title={supplyHint(item)}>
      {/* 칩 글자는 제 칸 안에서만 접힌다(불일치 표지처럼 길 때) — 장비 칩은 늘 첫 줄 오른쪽 끝이다 */}
      <span className="suppanel__chip">
        <SupplyChipContent item={item} />
      </span>
      {/*
       * 수치 — 장비 칩 바로 왼쪽(eric 2026-10-03 「0/9 를 어셈블러 왼쪽으로 옮겨줘.. 개 텍스트는 빼줘」). 종전엔 이름 바로 옆이었다 —
       * 이름 곁으로 되돌리지 마라. 경고 「▲」(없음 빨강 · 부족 노랑)는 수치 바로 오른쪽(장비 칩 앞)에 붙어 같이 간다
       */}
      <SupplyAmount item={item} summary={summary} />
      {/*
       * 장비 칩 — 머리 줄 맨 오른쪽(eric 2026-10-02 「어셈블러 칩을 맨 오른쪽에 넣어줘」 · 「보충」이 서던 자리).
       * 비전 칸의 LIVE 배지 자리다. 분류 칩이라 상태 색이 없다.
       */}
      <span className="suppanel__mod">{moduleName}</span>
    </FoldHead>
  )
  return (
    <li
      /* 카드의 접근 이름 — 소모품 이름 + 장비 이름(머리의 칩과 같은 글자) */
      aria-label={`${supplyName(item)} · ${moduleName}`}
      className={cx(
        'suppanel__item',
        low && 'suppanel__item--low',
        foldable && 'suppanel__item--fold',
      )}
      {...(foldable ? { onClick: () => setOpen((now) => !now) } : {})}
      data-low={low ? 'true' : undefined}
      data-module={item.module}
      data-mismatch={item.mismatch === true ? 'true' : undefined}
      data-origin={item.origin}
      data-supply={item.key}
    >
      {head}
      {noRecipe ? (
        /* 랙 칸이 하나도 없을 때(데크 표에 시약 랙이 없다)만 — 사유 한 줄 */
        <p className="supdetail__note">{t('monitoring.supply.noRecipe')}</p>
      ) : item.key === 'reagents' ? (
        /* 랙 칸이 없을 때 — 전 시약대 목록 + 맨 아래 범례(한 칸에 묶는다 · 카드는 두 칸 subgrid) */
        <div className="supreagent__pane">
          <ReagentList summary={summary} />
          <ReagentKeys />
        </div>
      ) : (
        <SupplySpecBody item={item} open={open} summary={summary} />
      )}
    </li>
  )
}

/**
 * **카드 머리 — 접고 펴는 칸이면 단추**(eric 2026-10-06 「처음에는.. 그림만 보여.. 해당 영역을 클릭하면.. 그림과 텍스트 같이」).
 * 카드 어디를 눌러도 펴고 접힌다(`li` 의 `onClick`) — 이 단추는 키보드 · 보조기술의 길이다(누름은 `li` 로 올라가 한 번만 바뀐다).
 * 🔴 팝업이 아니다 — 카드 안에서 그 자리에 펴진다(eric 2026-10-02 「클릭하면 팝업이 나오는데.. 안 나오게」는 그대로다).
 * 접을 것이 없는 칸은 종전 그대로 `div` 다.
 */
function FoldHead({
  foldable,
  open,
  title,
  children,
}: {
  foldable: boolean
  open: boolean
  title: string | undefined
  children: ReactNode
}) {
  if (!foldable)
    return (
      <div className="suppanel__head" title={title}>
        {children}
      </div>
    )
  /*
   * 🔴 **펴는 칸에만 꺾쇠(›)가 선다**(eric 2026-10-07 「누른다는 느낌이 없는데.. 누른다는 느낌이 있는 아이콘 같은 거 넣어줘」) —
   *    머리 줄 맨 끝(장비 칩 뒤). 새로 그리지 않고 접힌 줄 원자(`Disclosure`)의 `.a-disc__chev` 를 그대로 쓴다 — 접히면 오른쪽(›),
   *    펴면 아래를 향한다(`[aria-expanded='true'] > .a-disc__chev`). 꺾쇠는 `aria-hidden` 이고 「상세 보기」는 접근 이름 끝말로만 있다(눈에는 꺾쇠뿐 · eric 10-13).
   *    ⚠ 10-02 에 걷은 `›` 는 «팝오버를 여는» 표지였다 — 이 꺾쇠는 그 자리에서 펴지는 표지다(팝업은 여전히 없다).
   */
  return (
    <button aria-expanded={open} className="suppanel__head" title={title} type="button">
      {children}
      {/*
        🔴 눈에는 꺾쇠(›)만 — 「상세 보기」 글자는 보조기술 전용(`a-sr`)이다(eric 2026-10-13 「소모품에서.. > 이거만 표시하고.. 상세 보기
           텍스트는 없애줘」 · 10-12 에 보이게 했던 것을 되돌렸다). 단추의 접근 이름 끝말은 그대로 이 글자라 뜻이 사라지지 않는다
      */}
      <span className="suppanel__go">
        <span className="a-sr">{t('monitoring.supply.foldHint')}</span>
        <span aria-hidden="true" className="a-disc__chev" />
      </span>
    </button>
  )
}

/**
 * **tc rack · tip rack 칸 본문 — 랙 그림 | «구성» 목록**(eric 2026-10-06 「tc rack.. tip rack 이거랑. 이거 세개. .오른쪽에.. 해당 성분
 * 넣는 거 넣어줘.. 왼쪽에는 그림.. 오른쪽에는... 성분 말이야..」) — 시약 랙 칸과 같은 뼈대(`.supreagent__body--side` 1 : 1)다.
 * 🔴 목록의 값은 응답에 있는 것만이다(`supplySpecRows()`). 사유 한 줄(불일치 · 랙별 미수신)은 목록 아래에 선다.
 */
function SupplySpecBody({
  item,
  summary,
  open,
}: {
  item: SupplyItem
  summary: SupplySummary
  /** 펼쳤나 — 접혀 있으면 그림만(구성 목록 · 사유 한 줄 없음 · eric 2026-10-06) */
  open: boolean
}) {
  const rows = supplySpecRows(item)
  const note = slotGridNote(item)
  if (!open)
    return (
      <div className="supreagent__pane">
        <div className="supreagent__body">
          <SlotGrid bare item={item} summary={summary} />
        </div>
      </div>
    )
  return (
    <div className="supreagent__pane">
      <div className="supreagent__body supreagent__body--side">
        <SlotGrid bare item={item} summary={summary} />
        <div className="supspec">
          {rows.length === 0 ? null : (
            <dl
              aria-label={t('monitoring.supply.spec.list', { name: supplyName(item) })}
              className="supspec__list"
            >
              {rows.map((row) => (
                <div className="supspec__row" data-spec={row.key} key={row.key}>
                  <dt className="supspec__label">{row.label}</dt>
                  <dd className="supspec__value num">{row.value}</dd>
                </div>
              ))}
            </dl>
          )}
          {note === undefined ? null : <p className="supdetail__note">{note}</p>}
        </div>
      </div>
    </div>
  )
}

/** «구성» 목록 한 줄 — 이름표 · 값(둘 다 보이는 글자 · 보조기술도 같은 글자를 읽는다) */
interface SpecRow {
  key: string
  label: string
  value: string
}

/**
 * **tc rack · tip rack 의 «구성» 줄**(eric 2026-10-06) — 남은 · 쓴 · 전체 세 줄뿐이다(eric 2026-10-06 「남은 팁 쓴 팁 전체 이거만 남겨줘」 —
 * 이름 · 제조 · 팁 용량 · 팁 길이 · 쓴 칸 줄은 걷었다). 🔴 값은 응답에 있는 것만 — 모르면(`remaining` 이 `null`) 앞 둘은 뺀다.
 *
 * | 줄 | 출처 |
 * | --- | --- |
 * | 남은 팁 · 남은 TC / 쓴 팁 · 쓴 TC / 전체 | `supplySummary()` 의 `remaining` · `total`(장비 `tips_remaining` · `tips_by_rack` · `tc_remaining`) |
 *
 * ⚠ 장비 값이 안 와 «가득으로 본» 수(`origin: 'default'`)도 머리 수치와 같은 값이다 — 머리와 목록이 서로 다른 말을 하지 않는다
 *   (그 사실은 머리 툴팁 · 보조기술 글자가 말한다 · eric 2026-09-28).
 */
function supplySpecRows(item: SupplyItem): SpecRow[] {
  const tips = item.key === 'tips-1' || item.key === 'tips-2'
  const rows: SpecRow[] = []
  if (item.remaining !== null) {
    rows.push(
      {
        key: 'left',
        label: t(tips ? 'monitoring.supply.spec.tipsLeft' : 'monitoring.supply.spec.tcLeft'),
        value: NUM().format(item.remaining),
      },
      {
        key: 'used',
        label: t(tips ? 'monitoring.supply.spec.tipsUsed' : 'monitoring.supply.spec.tcUsed'),
        value: NUM().format(Math.max(0, item.total - item.remaining)),
      },
    )
  }
  rows.push({
    key: 'total',
    label: t('monitoring.supply.spec.total'),
    value: NUM().format(item.total),
  })
  return rows
}

/**
 * **시약 랙 칸 하나**(eric 2026-10-06 「시약은 (3)개 영역을 차지하게 되겠지?」) — 랙마다 칸 하나. 머리는 팁 · TC 칸과 같은 결이다:
 * 굵은 이름(· 보관 온도 · 응답 없음 표지) → 오른쪽 끝 수치(그 랙의 있음/시약 수 · `reagentRackCounts()`) → 장비 칩(염색기).
 * 🔴 수치는 시약 단위다(웰 수가 아니다 · 판 전체와 같은 셈). 눈에는 `1/2` · 보조기술은 「2개 중 1개 있음」. 시약이 없는 랙 · 도는 작업이
 *    없으면 「—」 — 사유는 보조기술 글자와 머리 툴팁(「도는 작업 없음」)이 말한다. 시약에는 «없음 · 부족» ▲ 판정이 없다(칸마다 있음 · 없음).
 * 🔴 접근 이름은 「tube rack 1 · 염색기」 — 칸마다 그 랙의 이름이다(종전 「시약 · 염색기」 한 카드).
 */
function RackItem({
  item,
  rack,
  summary,
  noRecipe,
}: {
  item: SupplyItem
  rack: ReagentRackChoice
  summary: SupplySummary
  noRecipe: boolean
}) {
  const moduleName = t(`module.${item.module}`)
  const name = rackChoiceName(rack)
  /* 접고 펴는 것은 그림이 있는 랙뿐이다 — 데크 표에 없는 시약대는 그림이 없어 목록이 곧 본문이다 */
  const foldable = rack.rack !== undefined
  const [open, setOpen] = useState(false)
  return (
    <li
      aria-label={`${name} · ${moduleName}`}
      className={cx('suppanel__item', 'suppanel__item--rack', foldable && 'suppanel__item--fold')}
      {...(foldable ? { onClick: () => setOpen((now) => !now) } : {})}
      data-module={item.module}
      data-origin={item.origin}
      data-rack={rack.key}
      data-supply={item.key}
    >
      <FoldHead foldable={foldable} open={open} title={supplyHint(item)}>
        <span className="suppanel__chip">
          <span className="supstrip__name">{name}</span>
          {/* 레시피 온도 먼저 · 없으면 냉장 시약대만 염색기 설정값(`ReagentRackChoice.storageCelsius`) · 둘 다 없으면 뺀다 */}
          {rack.storageCelsius === undefined ? null : <Storage celsius={rack.storageCelsius} />}
          {item.stale ? <StaleMark label={t('safety.moduleStale')} /> : null}
        </span>
        <RackAmount bay={rack.bay} noRecipe={noRecipe} />
        <span className="suppanel__mod">{moduleName}</span>
      </FoldHead>
      <ReagentRackBody
        name={name}
        noRecipe={noRecipe}
        open={!foldable || open}
        rack={rack}
        summary={summary}
      />
    </li>
  )
}

/**
 * 랙 칸 머리의 수치 — 그 랙의 있음인 시약 / 시약 수(`reagentRackCounts()`). 겉모습은 팁 · TC 머리 수치와 같은 글자(굵은 수 + 옅은 「/전체」).
 * 시약이 없는 랙(또는 도는 작업 없음)은 「—」 하나 — 시약 이름 · 수를 짓지 않는다.
 */
function RackAmount({ bay, noRecipe }: { bay: ReagentBay | undefined; noRecipe: boolean }) {
  const { present, total } = reagentRackCounts(bay)
  return (
    <span className="suppanel__amount">
      <span className="supstrip__amount num">
        {total <= 0 ? (
          <>
            <b aria-hidden="true" className="supstrip__value">
              {t('mask.none')}
            </b>
            <span className="a-sr">
              {noRecipe ? t('monitoring.supply.noRecipe') : t('monitoring.supply.rackEmpty')}
            </span>
          </>
        ) : (
          <>
            <span aria-hidden="true">
              <b className="supstrip__value">{NUM().format(present)}</b>
              <small className="supstrip__total">
                {t('monitoring.supply.ofTotal', { total: NUM().format(total) })}
              </small>
            </span>
            <span className="a-sr">{reagentPresentText({ remaining: present, total })}</span>
          </>
        )}
      </span>
    </span>
  )
}

/**
 * **시약 랙 칸 본문 — 랙 그림 | 그 랙의 용액 목록**(eric 2026-10-02 「시약에서... 왼쪽에 그림을 넣고.. 오른쪽에.. 용액 같은 거.. 표시해줘」).
 * 🔴 그림은 새 그림이 아니라 장비 탭 · tc rack · tip rack 칸과 같은 `DeckRackFigure`(`deckRacks.ts` 의 tuberack1 · tuberack2 · coldrack 줄의 `kind`)다.
 *    칸 상태는 칸 표지와 같은 판정(`reagentRackFigure()` ← `wellPresence()`)이다. 데크 표에 없는 시약대는 그릴 그림이 없어 목록만 선다.
 * 🔴 용액 목록은 정보를 빼지 않는다 — 칸이 좁아 길면 **목록 상자 안에서만** 구른다(판 · 칸은 구르지 않는다 · supplyPanel.css).
 * 🔴 카드는 머리 · 본문 두 칸 subgrid 다 — 본문은 한 칸(`.supreagent__pane`)에 묶어 세 번째 칸이 생기지 않게 한다.
 */
function ReagentRackBody({
  rack,
  name,
  summary,
  noRecipe,
  open,
}: {
  rack: ReagentRackChoice
  name: string
  summary: SupplySummary
  /**
   * 도는 작업이 없다(레시피 시약을 모른다 · eric 2026-10-03 「작업이 안 도니 어떤 항체 용액인지는 안 나오겠지만」) — 그림 · 축 라벨은
   * 그대로 서고, 용액 목록 자리는 사유 한 줄이다. 그림은 데크 기본 정의(번들 사본)에 칸 상태 없이 그림의 기본 모양이다 — 응답에 없는 상태를 짓지 않는다
   */
  noRecipe: boolean
  /** 펼쳤나 — 접혀 있으면 그림만(용액 목록 · 사유 한 줄 없음 · eric 2026-10-06) */
  open: boolean
}) {
  /* 그림 상자 키(`--sp-fig-h`) — 축 라벨 틀이 그림 키로 읽는다(`supplyPanel.css` · `100cqh` 대신) */
  const figRef = useBoxHeightVar<HTMLDivElement>('--sp-fig-h')
  return (
    <div className="supreagent__pane" data-rack={rack.key}>
      <div
        className={cx(
          'supreagent__body',
          open && rack.rack !== undefined && 'supreagent__body--side',
        )}
      >
        {rack.rack === undefined ? null : (
          /* 그림 상자 — tc rack · tip rack 칸의 회색 상자와 같은 면 · 모서리 · 여백(`.supreagent__fig`) */
          <div className="supreagent__fig" ref={figRef}>
            <RackAxesFrame definition={rack.bay?.definition} kind={rack.rack.kind}>
              <DeckRackFigure kind={rack.rack.kind} label={name} {...rackFigureProps(rack)} />
            </RackAxesFrame>
          </div>
        )}
        {!open ? null : noRecipe ? (
          /* 시약 이름을 지어내지 않는다 — 도는 작업이 없으면 어느 랙에 무엇이 놓였는지 응답에 없다 */
          <p className="supdetail__note supreagent__empty">{t('monitoring.supply.noRecipeRack')}</p>
        ) : rack.bay === undefined ? (
          <p className="supdetail__note supreagent__empty">{t('monitoring.supply.rackEmpty')}</p>
        ) : (
          <ReagentList bayHeads={false} bays={[rack.bay]} label={name} summary={summary} />
        )}
      </div>
    </div>
  )
}

/**
 * 랙 그림의 값 — 레시피 정의(있으면) · 칸 상태 · 시약 테. 그 랙에 도는 작업 시약이 없으면 아무것도 얹지 않는다
 * (그림의 기본대로 · 장비 탭 그림과 같다)
 */
function rackFigureProps(choice: ReagentRackChoice) {
  if (choice.bay === undefined) return {}
  const { occupancy, reagents } = reagentRackFigure(choice.bay)
  return {
    occupancy,
    reagents,
    ...(choice.bay.definition === undefined ? {} : { definition: choice.bay.definition }),
  }
}

/**
 * **시약 카드의 범례**(eric 2026-10-02 「시약 제목 표시줄에 범례 만들어줘. 동그라미 · 찬 동그라미 · 빗금 동그라미」 → 같은 날
 * 「있음 없음 미수신 이거.. reagent 맨 밑에 넣어줘.」) — 칸 표지(`ReagentList` 의 `.supreagent__well`)와 같은 점 · 같은 크기 + 글자.
 * 2026-10-06 부터 판 맨 아래 한 줄이다(랙 칸 셋이 같이 쓴다 · 랙 칸이 없을 때만 종전처럼 시약 카드 맨 아래). ⚠ 머리에 되살리지 마라.
 * 🔴 시약 뜻만 푼다 — 판 아래 범례 줄(팁 · TC 뜻)은 걷었다(eric 2026-10-02). 설명이라 가리지 않는다: 보조기술은 「있음 없음 미수신」을 읽는다.
 */
function ReagentKeys() {
  const keys = [
    { presence: 'present', text: t('monitoring.supply.presence.present') },
    { presence: 'absent', text: t('monitoring.supply.presence.absent') },
    { presence: 'unreceived', text: t('monitoring.supply.unreceived') },
  ] as const
  return (
    <p className="supreagent__keys">
      {keys.map((key) => (
        <span className="supreagent__key" data-presence={key.presence} key={key.presence}>
          <i aria-hidden="true" className="supreagent__dot" />
          {key.text}
        </span>
      ))}
    </p>
  )
}

/**
 * **항목 머리 왼쪽의 글자** — 이름 · 표지(≠ 불일치 · 응답 없음). 수치와 「▲」는 오른쪽 `SupplyAmount` 다(장비 칩 바로 앞).
 * 겉모습은 디자인팀 시안 2026-10-01(`DESIGN-CHANGES.md` §3 「소모품」 — 수치만 · 막대 없음). 클래스(`.supstrip__*`)는 종전
 * 요약 카드(`SupplyStrip`)의 이름 그대로다.
 *
 * ⚠ 종전에는 선택기 줄의 요약 카드(`SupplyStrip`)와 이 판이 이 한 벌을 같이 썼다. 요약 카드는 2026-10-02 에 걷었다(eric 「장비별
 *   보기에서.. 이게 보이는데.. 이거 없애줘.. 시료로 방금 오른쪽으로 뺐잖아」) — 이제 이 판 한 곳이다.
 * 🔴 시약 머리의 「미수신 N」은 걷었다(eric 2026-10-02 「reagent 옆에.. 미수신1은 없애줘」) · 칸별 기록과 합계가 어긋나면 두 팁 머리가
 *    합계 + 「≠ 불일치」.
 * 🔴 **팁 · TC 의 «가득으로 본 값»(장비 값 미수신)은 글자로 세우지 않는다**(eric 2026-09-28) — 툴팁 · 보조기술 글자만.
 */
function SupplyChipContent({ item }: { item: SupplyItem }) {
  const name = supplyName(item)
  const mismatch = item.mismatch === true
  /*
   * 값의 출처 — 팁 · TC 가 가득으로 «본» 값이면 그 사실(시약은 없다 · `supplyOriginText`). 🔴 칩에 글자로 세우지 않는다(eric 2026-09-28) —
   * 툴팁과 보조기술 이름으로만 남긴다. 판의 「값의 출처」 줄과 같은 문구다.
   */
  const origin = supplyOriginText(item)
  return (
    <>
      <span className="supstrip__name">{name}</span>
      {/* 출처는 보이지 않는 글자로만 — 칩 이름에 실려 보조기술이 읽는다(툴팁과 같은 문구) */}
      {origin === undefined ? null : <span className="a-sr">{origin}</span>}
      {/*
       * 시약 머리의 「미수신 N」은 걷었다(eric 2026-10-02 「reagent 옆에.. 미수신1은 없애줘」) — 미수신은 칸 표지 ▨ · 그림의 점선 칸 ·
       * 카드 맨 아래 범례가 말한다. 랙 선택지에도 넣지 않는다. 보조기술 글자에도 없다(칸마다 「A1 미수신」은 그대로). 되살리지 마라
       */}
      {mismatch ? (
        <span
          className="supstrip__flag supstrip__flag--mismatch supstrip__mark supstrip__mark--mismatch"
          title={t('monitoring.supply.tipsMismatchNote')}
        >
          <i aria-hidden="true">{MISMATCH_GLYPH}</i>
          {t('monitoring.supply.tipsMismatch')}
        </span>
      ) : null}
      {item.stale ? <StaleMark label={t('safety.moduleStale')} /> : null}
    </>
  )
}

/**
 * **항목 머리 오른쪽의 수치** — 「남음/전체」 · 「▲」(경고). 장비 칩 바로 왼쪽에 선다(eric 2026-10-03 「여기에서.. 0/9 를 어셈블러
 * 왼쪽으로 옮겨줘.. 개 텍스트는 빼줘」 — 종전 「tc rack 0/9개 …… 어셈블러」 → 「tc rack …… 0/9 어셈블러」).
 * 🔴 **눈에 보이는 「개」가 없다**(같은 날) — 단위는 보조기술 글자(「9개 중 0개 남음」 · 시약은 「8개 중 1개 있음」)에만 든다.
 * 🔴 **수치만 · 막대가 없다**(디자인팀 시안 2026-10-01). 경고 「▲」 하나만 **수치 오른쪽**(수치와 장비 칩 사이)에 덧붙는다(eric 2026-10-03
 *    「없거나.. 그러면.. 오른쪽에.. 노란색이나 빨간색.. 세모? 그걸로 해줘.」) — 0 이면 빨간 ▲(「없음」 · `isSupplyEmpty()`), 팁 20% 이하면
 *    노란 ▲(「부족」 · `isTipsLow()`), 넉넉하면 없다(판정은 `supplyAlert()` 한 곳). 🔴 **색만으로 말하지 않는다** — 눈에는 같은 ▲ 지만
 *    보조기술 글자 · 툴팁이 「없음」 / 「부족」을 말한다. 눈에 보이는 낱말은 세우지 않는다(eric 2026-10-02 「부족이라는 문구 빼줘」).
 * 🔴 **도는 작업이 없어 시약을 모르면 「—」**(eric 2026-09-28). 「도는 작업 없음」은 보조기술 글자 · 툴팁으로 남는다.
 * 🔴 시약 랙 칸의 수치는 이 함수가 아니라 `RackAmount` 다(랙마다 · 2026-10-06). 여기 시약 갈래는 랙 칸이 없을 때(판 전체)뿐이다.
 * ⚠ 값은 계산하지 않는다 — `supplySummary()` 가 준 것을 적는 법만 여기 있다.
 */
function SupplyAmount({ item, summary }: { item: SupplyItem; summary: SupplySummary }) {
  const noRecipe = item.key === 'reagents' && item.remaining === null
  const mismatch = item.mismatch === true
  const split = item.unit === 'count' && item.remaining === null && !mismatch
  const alert = supplyAlert(item)
  /* 시약 수치 — 판 전체의 있음/시약 수(랙 칸이 없을 때만 이 길을 탄다) */
  const reagentRemaining = item.remaining ?? 0
  const reagentTotal = item.total
  /*
   * 「남음/전체」 — 남은 수는 굵게, 「/전체」는 옅게. 시안 `.supi__v` + `small`. 눈에는 단위가 없다(eric 2026-10-03 「개 텍스트는 빼줘」) —
   * 보조기술은 `sr` 글자(단위 포함)를 읽고 보이는 글자는 가린다(두 번 안 읽히게).
   */
  const valueOf = (remaining: number, total: number, sr: string) => (
    <>
      <span aria-hidden="true">
        <b className="supstrip__value">{NUM().format(remaining)}</b>
        <small className="supstrip__total">
          {t('monitoring.supply.ofTotal', { total: NUM().format(total) })}
        </small>
      </span>
      <span className="a-sr">{sr}</span>
    </>
  )
  const amount = noRecipe ? (
    /* 🔴 도는 작업이 없어 시약을 모른다 — 「—」 하나(eric 2026-09-28). 사유는 툴팁 · 스크린리더 글자 */
    <>
      <b aria-hidden="true" className="supstrip__value">
        {t('mask.none')}
      </b>
      <span className="a-sr">{t('monitoring.supply.noRecipe')}</span>
    </>
  ) : mismatch ? (
    <span className="supstrip__sum">
      {summary.tipsReported === undefined
        ? t('mask.none')
        : t('monitoring.supply.tipsSum', summary.tipsReported)}
    </span>
  ) : split ? (
    <span className="supstrip__sum">{t('monitoring.supply.rackSplitUnknown')}</span>
  ) : item.unit === 'reagents' ? (
    reagentTotal <= 0 ? (
      <b className="supstrip__value">{t('mask.none')}</b>
    ) : (
      valueOf(
        reagentRemaining,
        reagentTotal,
        reagentPresentText({ remaining: reagentRemaining, total: reagentTotal }),
      )
    )
  ) : (
    valueOf(item.remaining ?? 0, item.total, remainingText(item))
  )
  const alertText = alert === undefined ? undefined : t(`monitoring.supply.${alert}`)
  return (
    <span className="suppanel__amount">
      <span className="supstrip__amount num">{amount}</span>
      {/*
       * 경고 — 수치 오른쪽 · 「▲」만 보인다(낱말은 보조기술 · 툴팁). 없음은 빨강(`--color-danger`), 부족은 노랑(주의 톤) —
       * 색은 `supply.css` 의 `.supstrip__flag--empty` · `--low` 가 정한다
       */}
      {alert === undefined ? null : (
        <span className={`supstrip__flag supstrip__flag--${alert}`} title={alertText}>
          <i aria-hidden="true">▲</i>
          <span className="a-sr">{alertText}</span>
        </span>
      )}
    </span>
  )
}
